import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n'
import { Badge } from '@/ui'
import { balanceVerdict, bpText, type BandRef, type RunBalance } from '../../api/process'
import { bandText, pctShort as pct } from '../../model/process/format'

/** Compact yield vs range for tables: coloured yield, the range under it. */
export function YieldCell({ balance }: { balance: RunBalance }) {
  const v = balanceVerdict(balance)
  const out = v.yield === 'BELOW' || v.yield === 'ABOVE'
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span className={cn('font-mono tabular', out && 'font-semibold text-danger', v.yield === 'IN_BAND' && 'text-ok')}>
        {bpText(balance.yieldBp)}
      </span>
      <span className="font-mono text-[11px] text-muted">{bandText(balance.band)}</span>
    </span>
  )
}

/** Compact left over for tables: kg and %, red when over the loss limit. */
export function LeftOverCell({ balance }: { balance: RunBalance }) {
  const fmt = useFormat()
  const v = balanceVerdict(balance)
  const bad = v.leftOver === 'OVER' || v.leftOver === 'SURPLUS' || v.leftOver === 'EXACT_ZERO'
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span className={cn('font-mono tabular', bad && 'font-semibold text-danger')}>
        {balance.residualG < 0 ? '−' : ''}
        {fmt.weight(Math.abs(balance.residualG))}
      </span>
      <span className="font-mono text-[11px] text-muted">
        {bpText(balance.residualBp)}
        {balance.lossLimitBp !== null && ` / ${bpText(balance.lossLimitBp)}`}
      </span>
    </span>
  )
}

/**
 * The yield on a scale with the range shaded: green marker inside the range, red outside.
 * The scale spans the range ± a margin so small misses are visible.
 */
export function YieldBar({ yieldBp, band }: { yieldBp: number | null; band: Pick<BandRef, 'minBp' | 'maxBp'> | null }) {
  if (yieldBp === null) return <p className="text-[13px] text-muted">No yield: nothing went in after the flush.</p>
  const hasBand = band && band.minBp !== null && band.maxBp !== null
  const lo = Math.max(0, Math.min(hasBand ? band.minBp! : yieldBp, yieldBp) - 500)
  const hi = Math.min(10000, Math.max(hasBand ? band.maxBp! : yieldBp, yieldBp) + 500)
  const span = Math.max(1, hi - lo)
  const at = (bp: number) => `${((bp - lo) / span) * 100}%`
  const inBand = hasBand && yieldBp >= band.minBp! && yieldBp <= band.maxBp!
  return (
    <div className="flex flex-col gap-1.5" role="img" aria-label={`Yield ${bpText(yieldBp)}, range ${bandText(band)}${hasBand ? (inBand ? ', inside' : ', outside') : ''}`}>
      <div className="relative h-3 rounded-full bg-paper-sunk">
        {hasBand && (
          <div
            className="absolute inset-y-0 rounded-full bg-ok/25 ring-1 ring-ok/40"
            style={{ left: at(band.minBp!), width: `${((band.maxBp! - band.minBp!) / span) * 100}%` }}
          />
        )}
        <div
          className={cn('absolute -top-1 h-5 w-1.5 -translate-x-1/2 rounded-full', !hasBand ? 'bg-muted' : inBand ? 'bg-ok' : 'bg-danger')}
          style={{ left: at(yieldBp) }}
        />
      </div>
      <div className="flex justify-between font-mono text-[11px] text-muted tabular">
        <span>{pct(lo)}%</span>
        {hasBand && <span>range {bandText(band)}</span>}
        <span>{pct(hi)}%</span>
      </div>
    </div>
  )
}

/** In range / out of range badge from the verdict. */
export function BandBadge({ balance }: { balance: RunBalance }) {
  const v = balanceVerdict(balance)
  if (v.yield === 'IN_BAND') return <Badge tone="ok">In range</Badge>
  if (v.yield === 'BELOW') return <Badge tone="danger">Below range</Badge>
  if (v.yield === 'ABOVE') return <Badge tone="danger">Above range</Badge>
  if (v.yield === 'NO_BAND') return <Badge tone="warn">No range set</Badge>
  return <Badge>Not judged</Badge>
}
